/* eslint-disable max-statements, max-depth */
import UploadFileIcon from '@mui/icons-material/Upload';
import {deepmerge} from '@mui/utils';
import {forwardRef, useEffect, useMemo, useRef, useState} from 'react';
import {FileRejection, useDropzone} from 'react-dropzone';
import {FormControl, FormHelperText} from '../Form';
import {InputLabel} from '../Input/InputLabel';
import {defaultTextMap} from './defaultTextMap';
import {FileInput} from './FileInput';
import {FileInputContext} from './FileInputContext';
import {RejectedFileAlert} from './subComponents/RejectedFileAlert';
import {FileInputProps} from './types/FileInputProps';
import {FileInputHandlers, FileInputState} from './types/FileInputState';
import {createValidatorObject} from './utils/createValidatorObject';
import {getErrorMessage} from './utils/getErrorMessage';
import {getFileStatus} from './utils/getFileStatus';

const defaultFileTypes = {
    'application/pdf': ['.pdf'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/csv': ['.csv']
};

export const FileInputField = forwardRef<HTMLDivElement, FileInputProps>(({
    accept = defaultFileTypes, minSize = 0, maxSize = 2000000, maxFiles = 5, disabled = false,
    validators = [], textMap: inputTextMap = undefined, id = 'file-input', ...props
}, ref) => {
    const [files, setFiles] = useState<FileInputState['files']>([]);
    const [rejectedFiles, setRejectedFiles] = useState<FileInputState['rejectedFiles']>([]);
    const [validations, setValidations] = useState<FileInputState['validations']>([]);
    const [status, setStatus] = useState<'inactive' | 'validating' | 'doneValidating'>('inactive');
    const isMounted = useRef(false);

    // create a set of validations for each 'file'
    const emptyValidations = useMemo(() => {
        const validatorObject = createValidatorObject(validators);

        return files.map(() => validatorObject);
    }, [files, validators]);

    const textMap = useMemo(() => {
        return deepmerge(defaultTextMap, inputTextMap);
    }, [inputTextMap]);

    const handleAddFiles: FileInputHandlers['handleAddFiles'] = (newFiles) => {
        setFiles(newFiles);
    };

    const handleAddRejectedFiles: FileInputHandlers['handleAddRejectedFiles'] = (newRejectedFiles) => {
        const textMappedRejections = newRejectedFiles.map((rejectedFile) => {
            rejectedFile.errors = rejectedFile.errors.map((e) => getErrorMessage(e.code, textMap));
            return rejectedFile;
        });

        setRejectedFiles(textMappedRejections);
    };

    const handleValidateFiles: FileInputHandlers['handleValidateFiles'] = async () => {
        let fileIndex = 0;

        const newValidations = emptyValidations;

        setStatus('validating');

        // if no validators, don't validate files
        if (!(validators.length > 0)) {
            setValidations(newValidations.map((validation) => {
                validation.status = 'success';

                return validation;
            }));

            setStatus('doneValidating');

            return undefined;
        }

        // validate files
        for await (const file of files) {
            // reset errors
            newValidations[fileIndex].errors = 0;

            // 2. For each file, loop through validators
            for await (const validator of validators) {
                // run validator
                const passedCheck = await new Promise<boolean>((resolve) =>
                    validator.validator(file, resolve)
                );

                // increase error count
                if (!passedCheck) {
                    newValidations[fileIndex].errors += 1;
                }

                // update validator check
                newValidations[fileIndex].checks = {
                    ...newValidations[fileIndex].checks,
                    [validator.key]: passedCheck
                };
            }

            // get file status based on # of errors
            const fileStatus = getFileStatus(newValidations[fileIndex].errors, validators.length);

            newValidations.splice(fileIndex, 1, {
                ...newValidations[fileIndex],
                status: fileStatus
            });

            setValidations([...newValidations]);

            fileIndex += 1;
        }

        setStatus('doneValidating');

        return undefined;
    };

    const handleRemoveFiles: FileInputHandlers['handleRemoveFiles'] = async (indexes): Promise<undefined> => {
        if (Array.isArray(indexes)) {
            for await (const index of indexes) {
                files.splice(index, 1);
                validations.splice(index, 1);
            }
        } else {
            files.splice(indexes, 1);
            validations.splice(indexes, 1);
        }

        setFiles([...files]);
        setValidations([...validations]);

        if (props.onRemoveFiles) {
            props.onRemoveFiles(indexes);
        }

        return undefined;
    };

    const handleClearFiles: FileInputHandlers['handleClearFiles'] = () => {
        setFiles([]);
        setRejectedFiles([]);
        setValidations([]);

        if (props.onClear) {
            props.onClear();
        }
    };

    // NOTE: Set dropzone state, returned state will be used in context
    const {
        open, ...dropzoneState
    } = useDropzone({
        accept,
        minSize,
        maxSize,
        maxFiles: 20, // internal max files for input
        disabled,

        // events
        onDrop: (droppedAcceptedFiles, droppedRejectedFiles, dropEvent) => {
            const allAcceptedFiles: File[] = [];
            const allRejectedFiles: FileRejection[] = [];

            if (files.length === maxFiles) {
                // Check if max files already reached
                // if yes, slice files and add to rejected Array
                allRejectedFiles.push(
                    ...droppedAcceptedFiles.map<FileRejection>((file) => ({
                        file,
                        errors: [{
                            code: 'TOO_MANY_FILES',
                            message: textMap.errorMessages.tooManyFiles
                        }]
                    })),
                    ...droppedRejectedFiles
                );
            } else if (droppedAcceptedFiles.length + files.length > maxFiles) {
                // 2. Check if new accepted file will exceed max files
                // get overflow index
                const sliceIndex = maxFiles - files.length;

                // Slice files and add to rejected Array
                allRejectedFiles.push(
                    ...droppedAcceptedFiles.slice(sliceIndex).map<FileRejection>((file) => ({
                        file,
                        errors: [{
                            code: 'TOO_MANY_FILES',
                            message: textMap.errorMessages.tooManyFiles
                        }]
                    })),
                    ...droppedRejectedFiles
                );

                // add left over to accepted
                allAcceptedFiles.push(...droppedAcceptedFiles.slice(0, sliceIndex), ...files);
            } else {
                allAcceptedFiles.push(...droppedAcceptedFiles, ...files);
                allRejectedFiles.push(...droppedRejectedFiles);
            }

            if (allAcceptedFiles.length > 0) {
                handleAddFiles(allAcceptedFiles);

                if (props.onDropAccepted) {
                    props.onDropAccepted(allAcceptedFiles, dropEvent);
                }
            }

            if (allRejectedFiles.length > 0) {
                handleAddRejectedFiles(allRejectedFiles);

                if (props.onDropRejected) {
                    props.onDropRejected(allRejectedFiles, dropEvent);
                }
            }
        },
        onFileDialogCancel: props.onFileDialogCancel,
        onFileDialogOpen: props.onFileDialogOpen,
        onDragEnter: props.onDragEnter,
        onDragOver: props.onDragOver,
        onDragLeave: props.onDragLeave,

        // controlled props
        useFsAccessApi: true,
        multiple: true,
        preventDropOnDocument: true,
        noClick: true,
        noKeyboard: false,
        noDrag: false,
        noDragEventsBubbling: true
    });

    useEffect(() => {
        // Solution for not running on component mount: https://stackoverflow.com/a/59841947
        if (isMounted.current) {
            handleValidateFiles();
        } else {
            isMounted.current = true;
        }
    }, [files, rejectedFiles]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (status === 'doneValidating' && props.onDropValidated) {
            props.onDropValidated({
                files,
                rejectedFiles,
                validators,
                validations
            });

            setStatus('inactive');
        }
    }, [validations, status]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <FileInputContext.Provider
            value={{
                accept,
                minSize,
                maxSize,
                maxFiles,
                disabled,

                DisplayIcon: props.DisplayIcon || UploadFileIcon,
                id,
                debug: Boolean(props.debug),
                error: Boolean(props.error),
                name: String(props.name),
                required: Boolean(props.required),
                inputProps: props.inputProps || {},
                hideValidationsPanel: Boolean(props.hideValidationsPanel),
                textMap,
                openFileDialog: open,
                showHelperText: Boolean(props.showHelperText),

                // state
                files,
                validators,
                rejectedFiles,
                validations,

                handleAddFiles,
                handleAddRejectedFiles,
                handleValidateFiles,
                handleRemoveFiles,
                handleClearFiles,

                ValidationPanelProps: props.ValidationPanelProps || {},

                ...dropzoneState
            }}
        >
            <FormControl
                disabled={disabled}
                error={props.error}
                fullWidth={true}
                hiddenLabel={props.hiddenLabel}
                ref={ref}
                required={props.required}
                sx={props.sx}
            >
                {props.label && (
                    <InputLabel
                        disabled={disabled}
                        error={props.error}
                        hiddenLabel={props.hiddenLabel}
                        htmlFor={id}
                    >
                        {props.label}
                    </InputLabel>
                )}
                <FileInput/>
                {props.helperText && (
                    <FormHelperText {...props.FormHelperTextProps}>
                        {props.helperText}
                    </FormHelperText>
                )}
            </FormControl>
            {!props.hideRejectedFiles && Array.isArray(rejectedFiles) && rejectedFiles.length > 0 && (
                <RejectedFileAlert/>
            )}
        </FileInputContext.Provider>
    );
});

FileInputField.displayName = 'FileInputField';
